import { Box } from '@mui/system';
import { Formik, FormikProps, useFormikContext } from 'formik';
import * as yup from 'yup';
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import ButtonComponent from '../../../components/formlib/ButtonComponent';
import CommentTextbox from '../../../components/formlib/CommentTextbox';
import MediumTypography from '../../../components/formlib/MediumTypography';
import {
  AlertColor,
  Grid,
  Paper,
  Table,
  TableBody,
  TableContainer,
} from '@mui/material';
import { HeadCell, TableHeader } from '../../../components/formlib/TableHeader';
import DropdownComponent from '../../../components/formlib/DropdownComponent';
import DatePickerComponent from '../../../components/formlib/DatePickerComponent';
import Textinput from '../../../components/formlib/Textinput';
import DurationPicker from '../../../components/formlib/DurationPicker';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { OptionType, Order } from '../../../utils/type';
import { LookUpContext } from '../../../context/LookUpContextProvider';
import InformationModal from '../../ifsp/InformationModal';

import { ReactComponent as DownArrow } from '../../../assets/images/downarrowcolored.svg';
import { ReactComponent as UpArrow } from '../../../assets/images/uparrowcolored.svg';
import { ReactComponent as InfoIcon } from '../../../assets/images/infoIcon.svg';
import { ReactComponent as AddNew } from '../../../assets/images/Plus.svg';
import { ReactComponent as Delete } from '../../../assets/images/deleteAlertIcon.svg';
import dayjs, { Dayjs } from 'dayjs';
import {
  ServicePlanType,
  updateIfspOriginalSDPAndConsent,
  updateIfspSDPAndConsent,
} from '../../../redux/features/ifsp/ifspSDPAndConsentSlice';
import {
  datePickerMinDateMaxDateValidate,
  formatStringDateInDecimals,
  formatTimeDurationInDecimals,
} from '../../../utils/dateUtil';
import ServiceDeliveryPlanTableRow from './sDPAndConsentsComponents/ServiceDeliveryPlanTableRow';
import ModalPopup from '../../../components/formlib/ModalPopup';
import {
  deleteIfspService,
  deleteServiceDeliveryPlan,
} from '../../../services/ifspClient/IfspApi';
import { LoaderContext, LoaderContextType } from '../../../layouts/AppSidebar';
import SnackBarComponent from '../../../components/formlib/SnackBarComponent';
import { updateIfspUnsavedFields } from '../../../redux/features/ifsp/ifspUnsavedFieldSlice';

const info =
  'This page identifies the services that are necessary to meet the unique need(s) of the child and family in order to achieve the measurable results or outcomes. The provider of each EI service should be identified by discipline and the location of each service should indicate the natural environment such as home, childcare, playgroups, and other community settings. All services are provided in a natural setting to the extent possible. Changes in specific EI services, frequency, or location require parental consent. EI services are paid for by the Department of Public Health through state and federal funds or public/private health insurance with parental consent.';

export interface TableData {
  eiService: string;
  providedBy: string;
  locationOfService: string;
  freqperlen: string;
  startDate: string;
  intensity: string;
  duration: string;
  endDate: string;
  reason: string;
  actions: string;
}

export const headCells: HeadCell<TableData>[] = [
  {
    id: 'eiService',
    labelId: 'EI Service/Method',
    defaultLabelId: 'EI Service/Method',
    numeric: false,
    requiredSorting: true,
  },
  {
    id: 'providedBy',
    labelId: 'Provided by (Discipline Responsibile)',
    defaultLabelId: 'Provided by (Discipline Responsibile)',
    numeric: false,
    requiredSorting: true,
  },
  {
    id: 'locationOfService',
    labelId: 'Location of Service',
    defaultLabelId: 'Location of Service',
    numeric: false,
    requiredSorting: false,
  },
  {
    id: 'freqperlen',
    labelId: 'IFSP.FreqPerLen',
    defaultLabelId: 'Freq, Per, Len',
    numeric: false,
    requiredSorting: false,
  },
  {
    id: 'intensity',
    labelId: 'Intensity - Individual/Group',
    defaultLabelId: 'Intensity - Individual/Group',
    numeric: false,
    requiredSorting: false,
  },
  {
    id: 'duration',
    labelId: 'Duration of Service',
    defaultLabelId: 'Duration of Service',
    numeric: false,
    requiredSorting: false,
  },
  {
    id: 'startDate',
    labelId: 'Start Date',
    defaultLabelId: 'Start Date',
    numeric: false,
    requiredSorting: true,
  },
  {
    id: 'endDate',
    labelId: 'End Date',
    defaultLabelId: 'End Date',
    numeric: false,
    requiredSorting: true,
  },
  {
    id: 'reason',
    labelId: 'Reason',
    defaultLabelId: 'Reason',
    numeric: false,
    requiredSorting: false,
  },
  {
    id: 'actions',
    labelId: 'Actions',
    defaultLabelId: 'Actions',
    numeric: false,
    requiredSorting: false,
  },
];

const TextAreaStyle: React.CSSProperties = {
  minWidth: '840px',
  width: '840px',
};

const EXCLUDED_CODES = [
  'CHILDGROUPCOMMUNITY',
  'CHILDGROUPEIONLY',
  'PARENTGROUP',
  'TRANSPORTATION',
];

const validationSchema = yup.object().shape({
  eiService: yup.string().required('IFSP.EIServiceisrequired'),
  frequency: yup
    .string()
    .required('IFSP.Frequencyisrequired')
    .matches(/^[1-9]$|^10$/, 'IFSP.FrequencyisGreaterThanZeroAndLessThanTen'),
  length: yup.string().required('IFSP.Lengthisrequired'),
  intensity: yup.string().required('IFSP.Intensityisrequired'),
  period: yup.string().required('IFSP.Periodisrequired'),
  locationOfService: yup.string().required('IFSP.LocationofServiceisrequired'),
  providedBy: yup
    .string()
    .nullable()
    .when('eiService', {
      is: (val: string) => !EXCLUDED_CODES.includes(val),
      then: (schema) => schema.required('IFSP.Providedbyisrequired'),
      otherwise: (schema) => schema.notRequired(),
    }),
  startDate: yup
    .date()
    .nullable()
    .typeError('Date is required')
    .required('IFSP.StartDateisrequired'),
  endDate: yup.date().nullable().typeError('Date is required'),
  durationOfService: yup.string().required('IFSP.DurationofServiceisrequired'),
});

export type ServiceDeliveryPlanType = {
  isUnsavedPopupConfirmed?: boolean;
};

const ServiceDeliveryPlan = ({
  isUnsavedPopupConfirmed,
}: ServiceDeliveryPlanType) => {
  const ifspMainPageDetails = useAppSelector(
    (state) => state.ifspClientDetails.ifspClientDetails,
  );

  const ifspSDPAndConsent = useAppSelector(
    (state) => state.ifspSDPAndConsent.sdpAndConsent,
  );

  const ifspOriginalSDPAndConsent = useAppSelector(
    (state) => state.ifspSDPAndConsent.originalSdpAndConsent,
  );

  const ifspFirstMeetingDate = useAppSelector(
    (state) => state.ifspClientDetails.ifspClientDetails.ifspFirstMeetingDate,
  );

  const ifspPermission = useAppSelector((state) => state.ifspPermissionDetails);
  const ifspUnsavedFields = useAppSelector((state) => state.ifspUnsavedFields);
  const dispatch = useAppDispatch();
  const [showSubSection, setShowSubSection] = useState<boolean>(true);
  const [openIndex, setOpenIndex] = useState<number>(0);
  const [openedSubSectionIndex, setOpenedSubSectionIndex] = useState<number>(0);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [editIndex, setEditIndex] = useState<number>(0);

  const [eiService, setEiService] = useState<OptionType[]>([]);
  const [extractedEiService, setExtractedEiService] = useState<OptionType[]>(
    [],
  );
  const [eiServiceAutism, setEiServiceAutism] = useState<OptionType[]>([]);
  const [locationOfService, setLocationOfService] = useState<OptionType[]>([]);
  const [period, setPeriod] = useState<OptionType[]>([]);
  const [intensity, setIntensity] = useState<OptionType[]>([]);
  const [providedBy, setProvidedBy] = useState<OptionType[]>([]);
  const [providerDiscipline, setProviderDiscipline] = useState<OptionType[]>(
    [],
  );
  const [lookUpReasons, setLookUpReasons] = useState<OptionType[]>([]);
  const [fliteredLookUpReasons, setFliteredLookUpReasons] = useState<
    OptionType[]
  >([]);
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<keyof TableData | ''>('');
  const [infoSDPModal, setInfoSDPModal] = useState<boolean>(false);
  const [openAccordian, setOpenAccordian] = useState<boolean>(true);
  const [openSDPDeleteWarningModal, setOpenSDPDeleteWarningModal] =
    useState<boolean>(false);
  const [openServiceDeleteWarningModal, setOpenServiceDeleteWarningModal] =
    useState<boolean>(false);
  const [deleteServiceIdAndIndex, setDeleteServiceIdAndIndex] = useState<{
    serviceId: string;
    index: number;
    subSectionIndex: number;
  }>({ serviceId: '', index: 0, subSectionIndex: 0 });
  const [open, setOpen] = useState<boolean>(false);
  const [toastrVariable, setToastrVariable] = useState<AlertColor>('info');
  const [toastrDefaultMessage, setToastrDefaultMessage] = useState('');
  const [toastrId, setToastrId] = useState('');

  const [providedByAutism, setProvidedByAutism] = useState<OptionType[]>([]);
  const { toggleLoader } = useContext(LoaderContext) as LoaderContextType;

  const { lookups } = useContext(LookUpContext);
  const formikRef = useRef<FormikProps<ServicePlanType>>(null);
  const UnsavedChangesWatcher = ({
    onChange,
  }: {
    onChange: (dirty: boolean) => void;
  }) => {
    const { dirty } = useFormikContext<ServicePlanType>();

    useEffect(() => {
      onChange(dirty);
    }, [dirty, onChange]);

    return null;
  };
  useEffect(() => {
    if (lookups === null) {
      return;
    }

    setLocationOfService(() => {
      return lookups.locationOfService?.map((l) => {
        return {
          label: l.description,
          id: l.code,
        };
      });
    });

    setPeriod(() => {
      return lookups.period?.map((p) => {
        return {
          label: p.description,
          id: p.code,
        };
      });
    });

    setProviderDiscipline(() => {
      return lookups.ifspDiscipline?.map((d) => {
        return {
          label: d.description,
          id: d.code,
        };
      });
    });

    setIntensity(() => {
      return lookups.intensity?.map((i) => {
        return {
          label: i.description,
          id: i.code,
        };
      });
    });
    setLookUpReasons(() => {
      return lookups.ifspServicePlanReason?.map((e) => {
        return {
          label: e.description,
          id: e.code,
        };
      });
    });
    setFliteredLookUpReasons(() => {
      return lookups.ifspServicePlanReason?.map((e) => {
        return {
          label: e.description,
          id: e.code,
        };
      });
    });
  }, [lookups]);

  useEffect(() => {
    if (isUnsavedPopupConfirmed) {
      formikRef.current?.resetForm();
    }
  }, [isUnsavedPopupConfirmed]);

  useEffect(() => {
    if (lookups === null) {
      return;
    }

    const earlyIntervention =
      ifspSDPAndConsent.serviceDelivery?.[openIndex]?.servicePlan?.[editIndex];

    setProvidedBy(() => {
      return lookups.ifspDiscipline
        .filter((e) => {
          if (e.active && !e.isAutism) return true;

          if (
            isEdit &&
            e.code ===
              ifspSDPAndConsent.serviceDelivery[openIndex].servicePlan[
                editIndex
              ].providedBy
          )
            return true;

          return false;
        })
        .map((data) => {
          return {
            label: data.description,
            id: data.code,
          };
        });
    });

    setProvidedByAutism(() => {
      return lookups.ifspDiscipline
        .filter((e) => {
          if (e.active && e.isAutism) return true;

          if (
            isEdit &&
            e.code ===
              ifspSDPAndConsent.serviceDelivery[openIndex].servicePlan[
                editIndex
              ].providedBy
          )
            return true;

          return false;
        })
        .map((data) => {
          return {
            label: data.description,
            id: data.code,
          };
        });
    });

    const actualEiService = isEdit
      ? lookups.eiService?.filter((e) => {
          return e.code === earlyIntervention?.eiService || !e.isAutism;
        })
      : lookups.eiService?.filter((e) => !e.isAutism);

    setExtractedEiService(() => {
      return (
        actualEiService
          ?.filter((e) => {
            if (e.active) return true;

            if (
              isEdit &&
              e.code ===
                ifspSDPAndConsent.serviceDelivery[openIndex].servicePlan[
                  editIndex
                ].eiService
            )
              return true;

            return false;
          })
          .map((e) => ({
            label: e.description,
            id: e.code,
          })) ?? []
      );
    });

    setEiService(() => {
      return lookups.eiService?.map((e) => ({
        label: e.description,
        id: e.code,
      }));
    });

    setEiServiceAutism(() => {
      return lookups.eiService
        .filter((e) => {
          if (e.active && e.isAutism) return true;

          if (
            isEdit &&
            e.code ===
              ifspSDPAndConsent.serviceDelivery[openIndex].servicePlan[
                editIndex
              ].eiService
          )
            return true;

          return false;
        })
        .map((data) => {
          return {
            label: data.description,
            id: data.code,
          };
        });
    });

    setFliteredLookUpReasons(() => {
      return lookups.ifspServicePlanReason
        ?.filter((e) => {
          if (e.active) return true;
          if (
            isEdit &&
            e.code ===
              ifspSDPAndConsent.serviceDelivery[openIndex].servicePlan[
                editIndex
              ].reasonForLate
          )
            return true;
          return false;
        })
        .map((e) => {
          return {
            label: e.description,
            id: e.code,
          };
        });
    });
  }, [lookups, isEdit, editIndex, openedSubSectionIndex, openIndex]);

  const handleRequestSort = (
    _event: React.MouseEvent<unknown>,
    property: keyof TableData,
  ) => {
    setOrder(order === 'asc' ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const validateDate = (values: ServicePlanType) => {
    const errors: Partial<ServicePlanType> = {};

    if (values.startDate) {
      if (dayjs(values.startDate).isBefore(ifspFirstMeetingDate)) {
        errors.startDate = 'IFSP.StartDateCantBeGreaterThanIfspMeetingDate';
      }
      if (
        ifspSDPAndConsent.referralContactDate &&
        dayjs(values.startDate).isBefore(
          dayjs(ifspSDPAndConsent.referralContactDate),
        )
      ) {
        errors.startDate = 'IFSP.StartDateCantBeGreaterThanReferralContactDate';
      }
    }

    if (values.startDate && values.endDate) {
      const startDate = dayjs(values.startDate, 'MM/DD/YYYY');
      const endDate = dayjs(values.endDate, 'MM/DD/YYYY');

      if (startDate.isAfter(endDate)) {
        errors.endDate = 'BlockTime.endDateValidationError';
      }
    }

    if (datePickerMinDateMaxDateValidate(values.startDate)) {
      errors.startDate = 'datePickerMinDateMaxDateValidate';
    }
    if (datePickerMinDateMaxDateValidate(values.endDate)) {
      errors.endDate = 'datePickerMinDateMaxDateValidate';
    }
    return errors;
  };

  const sortServices = (
    services: ServicePlanType[],
    orderB: string,
    orde: 'asc' | 'desc',
  ) => {
    return [...services].sort((a, b) => {
      if (orderB === 'eiService') {
        return orde === 'asc'
          ? a.eiService.localeCompare(b.eiService)
          : b.eiService.localeCompare(a.eiService);
      } else if (orderB === 'providedBy') {
        return orde === 'asc'
          ? a.providedBy.localeCompare(b.providedBy)
          : b.providedBy.localeCompare(a.providedBy);
      } else if (orderB === 'startDate') {
        const aDate = a.startDate ? dayjs(a.startDate).toISOString() : '';
        const bDate = b.startDate ? dayjs(b.startDate).toISOString() : '';
        return orde === 'asc'
          ? aDate.localeCompare(bDate)
          : bDate.localeCompare(aDate);
      } else if (orderB === 'endDate') {
        const aDate = a.endDate ? dayjs(a.endDate).toISOString() : '';
        const bDate = b.endDate ? dayjs(b.endDate).toISOString() : '';
        return orde === 'asc'
          ? aDate.localeCompare(bDate)
          : bDate.localeCompare(aDate);
      }
      return 0;
    });
  };

  const formattedLength = (value: number) => {
    return /[.:]/.test(String(value)) ? String(value) : `${value}:00`;
  };
  const onEditRow = (subSectionIndex: number, index: number) => {
    setEditIndex(index);
    const editableData =
      ifspSDPAndConsent.serviceDelivery[subSectionIndex].servicePlan[index];
    formikRef.current?.setValues({
      id: editableData.id,
      durationOfService: editableData.durationOfService,
      eiService: editableData.eiService,
      providedBy: editableData.providedBy,
      locationOfService: editableData.locationOfService,
      length: editableData.length,
      frequency: editableData.frequency,
      period: editableData.period,
      intensity: editableData.intensity,
      startDate: editableData.startDate,
      endDate: editableData.endDate,
      reasonForLate: editableData.reasonForLate,
      tbrServicePlanId: editableData.tbrServicePlanId,
      tbrUpdateRequired: editableData.tbrUpdateRequired,
    });
    setIsEdit(true);
  };

  const onDeleteRowData = (
    id: string,
    subSectionIndex: number,
    index: number,
  ) => {
    setOpenServiceDeleteWarningModal(true);
    setDeleteServiceIdAndIndex({ serviceId: id, index, subSectionIndex });
  };

  const handleDeleteService = () => {
    toggleLoader(true);
    if (deleteServiceIdAndIndex.serviceId) {
      deleteIfspService(deleteServiceIdAndIndex.serviceId)
        .then(() => {
          setOpen(true);
          setToastrVariable('success');
          setToastrId('IFSP.Service.DeleteSuccess');
          setToastrDefaultMessage('Service deleted successfully');
          dispatch(
            updateIfspSDPAndConsent({
              ...ifspSDPAndConsent,
              serviceDelivery: ifspSDPAndConsent.serviceDelivery.map(
                (sd, sdIndex) => {
                  if (sdIndex === deleteServiceIdAndIndex.subSectionIndex) {
                    return {
                      ...sd,
                      servicePlan: sd.servicePlan.filter(
                        (sp, spIndex) =>
                          spIndex !== deleteServiceIdAndIndex.index,
                      ),
                    };
                  }
                  return sd;
                },
              ),
            }),
          );
          dispatch(
            updateIfspOriginalSDPAndConsent({
              ...ifspOriginalSDPAndConsent,
              serviceDelivery: ifspOriginalSDPAndConsent.serviceDelivery.map(
                (sd, sdIndex) => {
                  if (sdIndex === deleteServiceIdAndIndex.subSectionIndex) {
                    return {
                      ...sd,
                      servicePlan: sd.servicePlan.filter(
                        (sp, spIndex) =>
                          spIndex !== deleteServiceIdAndIndex.index,
                      ),
                    };
                  }
                  return sd;
                },
              ),
            }),
          );
        })
        .catch(() => {
          setOpen(true);
          setToastrVariable('error');
          setToastrId('IFSP.Service.DeleteError');
          setToastrDefaultMessage('Failed to delete service');
        })
        .finally(() => {
          toggleLoader(false);
          setOpenServiceDeleteWarningModal(false);
        });
    } else {
      dispatch(
        updateIfspSDPAndConsent({
          ...ifspSDPAndConsent,
          serviceDelivery: ifspSDPAndConsent.serviceDelivery.map(
            (sd, sdIndex) => {
              if (sdIndex === deleteServiceIdAndIndex.subSectionIndex) {
                return {
                  ...sd,
                  servicePlan: sd.servicePlan.filter(
                    (sp, spIndex) => spIndex !== deleteServiceIdAndIndex.index,
                  ),
                };
              }
              return sd;
            },
          ),
        }),
      );
      toggleLoader(false);
      setOpenServiceDeleteWarningModal(false);
    }
  };

  const handleAddServiceDeliveryPlan = (
    subSectionIndex: number,
    values: ServicePlanType,
  ) => {
    if (isEdit) {
      const updatedSdpData = ifspSDPAndConsent.serviceDelivery[
        subSectionIndex
      ].servicePlan.map(
        (earlyInterventionService, earlyInterventionServiceIndex) =>
          earlyInterventionServiceIndex === editIndex
            ? {
                ...earlyInterventionService,
                eiService: values.eiService,
                providedBy: values.providedBy,
                length: values.length,
                period: values.period,
                locationOfService: values.locationOfService,
                frequency: values.frequency,
                durationOfService: values.durationOfService,
                intensity: values.intensity,
                startDate: values.startDate,
                endDate: values.endDate,
                reasonForLate: values.reasonForLate,
                tbrServicePlanId: values.tbrServicePlanId,
                tbrUpdateRequired: values.tbrUpdateRequired,
              }
            : earlyInterventionService,
      );
      dispatch(
        updateIfspSDPAndConsent({
          ...ifspSDPAndConsent,
          serviceDelivery: ifspSDPAndConsent.serviceDelivery.map(
            (subSection, index) => {
              if (index !== subSectionIndex) {
                return subSection;
              }
              return {
                ...subSection,
                servicePlan: sortServices(updatedSdpData, orderBy, order),
              };
            },
          ),
        }),
      );
      setIsEdit(false);
    } else {
      dispatch(
        updateIfspSDPAndConsent({
          ...ifspSDPAndConsent,
          serviceDelivery: ifspSDPAndConsent.serviceDelivery.map(
            (subSection, index) => {
              if (index !== subSectionIndex) return subSection;
              return {
                ...subSection,
                servicePlan: sortServices(
                  [...subSection.servicePlan, values],
                  orderBy,
                  order,
                ),
              };
            },
          ),
        }),
      );
    }
  };

  const addSubSection = () => {
    dispatch(
      updateIfspSDPAndConsent({
        ...ifspSDPAndConsent,
        serviceDelivery: [
          ...ifspSDPAndConsent.serviceDelivery,
          {
            ifspId: ifspSDPAndConsent.ifspId || '',
            id: '',
            eiServiceJustificationComment: '',
            serviceSupportComment: '',
            serviceSupportNeededComment: '',
            serviceDeliveryType: '',
            servicePlan: [],
          },
        ],
      }),
    );
    formikRef.current?.setTouched({});
    formikRef.current?.resetForm();
    setOpenIndex(1);
  };

  const handleDeleteSDP = () => {
    toggleLoader(true);
    const isIdExist = ifspSDPAndConsent.serviceDelivery[1].id;
    const newSdpData = ifspSDPAndConsent.serviceDelivery.slice(0, -1);
    if (isIdExist) {
      deleteServiceDeliveryPlan(ifspSDPAndConsent.serviceDelivery[1].id)
        .then(() => {
          setOpen(true);
          setToastrVariable('success');
          setToastrId('IFSP.Service.DeleteSuccess');
          setToastrDefaultMessage('Service deleted successfully');
          dispatch(
            updateIfspSDPAndConsent({
              ...ifspSDPAndConsent,
              serviceDelivery: newSdpData,
            }),
          );
          dispatch(
            updateIfspOriginalSDPAndConsent({
              ...ifspOriginalSDPAndConsent,
              serviceDelivery: newSdpData,
            }),
          );
        })
        .catch(() => {
          setOpen(true);
          setToastrVariable('error');
          setToastrId('IFSP.Service.DeleteFailed');
          setToastrDefaultMessage('Failed to delete service');
        })
        .finally(() => {
          setOpenIndex(0);
          toggleLoader(false);
          setOpenSDPDeleteWarningModal(false);
        });
    } else {
      dispatch(
        updateIfspSDPAndConsent({
          ...ifspSDPAndConsent,
          serviceDelivery: newSdpData,
        }),
      );
      toggleLoader(false);
      setOpenSDPDeleteWarningModal(false);
      setOpenIndex(0);
    }
  };

  const handleCloseSnakBar = (
    event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };

  const isUpdateServiceToPlan =
    isEdit ||
    (ifspPermission.hasIfspEditPermissionAfterLock &&
      ifspSDPAndConsent.isLocked);

  const addlabelid = isUpdateServiceToPlan
    ? 'IFSP.UpdateServiceToPlan'
    : 'IFSP.SaveServiceToPlan';

  const adddefaultlabel = isUpdateServiceToPlan
    ? 'Update Service to Plan'
    : 'Save Service to Plan';

  const ifspUnsavedFieldsRef = useRef(ifspUnsavedFields);

  useEffect(() => {
    ifspUnsavedFieldsRef.current = ifspUnsavedFields;
  }, [ifspUnsavedFields]);

  const handleUpdateUnsavedChanges = useCallback(
    (hasChanges: boolean) => {
      const current = ifspUnsavedFieldsRef.current;

      if (current.isServiceDeliveryFieldsChanged === hasChanges) {
        return;
      }

      dispatch(
        updateIfspUnsavedFields({
          ...current,
          isServiceDeliveryFieldsChanged: hasChanges,
        }),
      );
    },
    [dispatch],
  );

  return (
    <Box component="main">
      <Box component="section">
        <Box component="div">
          <Formik
            initialValues={{
              id: '',
              tbrServicePlanId: 0,
              eiService: '',
              providedBy: '',
              durationOfService: '',
              frequency: null,
              period: '',
              locationOfService: '',
              intensity: '',
              length: null,
              startDate: '',
              endDate: '',
              reasonForLate: '',
              tbrUpdateRequired: false,
            }}
            innerRef={formikRef}
            validationSchema={validationSchema}
            validate={validateDate}
            onSubmit={(values, { resetForm }) => {
              const selectedService = eiService.find(
                (item) => item.id === values.eiService,
              );
              const selectedProvider = providerDiscipline.find(
                (item) => item.id === values.providedBy,
              );
              const selectedLocationService = locationOfService.find(
                (item) => item.id === values.locationOfService,
              );
              const selectedIntensity = intensity.find(
                (item) => item.id === values.intensity,
              );
              const selectedPeriod = period.find(
                (item) => item.id === values.period,
              );

              handleAddServiceDeliveryPlan(openedSubSectionIndex, {
                ...values,
                eiService: selectedService?.id || '',
                providedBy: selectedProvider?.id || '',
                period: selectedPeriod?.id || '',
                locationOfService: selectedLocationService?.id || '',
                intensity: selectedIntensity?.id || '',
              });
              resetForm();
            }}
          >
            {({ values, setFieldValue, errors, touched, handleSubmit }) => (
              <Box component="div">
                <UnsavedChangesWatcher onChange={handleUpdateUnsavedChanges} />
                <SnackBarComponent
                  open={open}
                  handleClose={handleCloseSnakBar}
                  successOrError={toastrVariable}
                  labelId={toastrId}
                  defaultMessageId={toastrDefaultMessage}
                />
                <InformationModal
                  open={infoSDPModal}
                  titleLabel="IFSP.Information"
                  titleDefaultLabel="Information"
                  buttonLabel="IFSP.InfoOkButton"
                  buttonDefaultLabel="Ok"
                  onClose={() => {
                    setInfoSDPModal(false);
                  }}
                  infoLabelId="IFSP.SDPInfo"
                  infoDefaultLabel={info}
                />
                <ModalPopup
                  open={openServiceDeleteWarningModal}
                  description="permanentDeleteConfirmationMessage"
                  onOk={() => {
                    handleDeleteService();
                  }}
                  onCancel={() => {
                    setOpenServiceDeleteWarningModal(false);
                  }}
                  labelId1="Clientpage.cancelbtn"
                  negativeActionLabel="cancelText"
                  labelId2="Clientpage.Okbtn"
                  positiveActionLabel="Ok"
                />
                <ModalPopup
                  open={openSDPDeleteWarningModal}
                  description="permanentDeleteConfirmationMessage"
                  onOk={() => {
                    handleDeleteSDP();
                  }}
                  onCancel={() => {
                    setOpenSDPDeleteWarningModal(false);
                  }}
                  labelId1="Clientpage.cancelbtn"
                  negativeActionLabel="cancelText"
                  labelId2="Clientpage.Okbtn"
                  positiveActionLabel="Ok"
                />
                <Box
                  component="div"
                  className="p-lg borderRadius4"
                  sx={{
                    border:
                      ifspMainPageDetails.serviceDeliveryTbrUpdateRequired &&
                      !ifspMainPageDetails.isServiceDeliveryChecked
                        ? '1px solid #F96300'
                        : '1px solid #00C6B8',
                    backgroundColor: '#ECF9F8',
                  }}
                >
                  <Box
                    component="div"
                    className="flex__justify__space-between"
                    sx={{
                      paddingBottom:
                        openAccordian &&
                        ifspSDPAndConsent.serviceDelivery?.length > 0
                          ? '24px'
                          : 0,
                      borderBottom:
                        openAccordian &&
                        ifspSDPAndConsent.serviceDelivery?.length > 0
                          ? '1px solid #00C6B8'
                          : 'none',
                    }}
                  >
                    <Box className="flex__ ">
                      <MediumTypography
                        labelid="IFSP.session6"
                        defaultlabel="Service Delivery Plan"
                        fontSize="18px"
                        fontweight={600}
                        textColor="#2A4241"
                        sxProps={{
                          marginBottom:
                            openAccordian &&
                            ifspSDPAndConsent.serviceDelivery?.length < 1
                              ? '24px'
                              : '0px',
                        }}
                      />
                      {openAccordian && (
                        <InfoIcon
                          onClick={() => {
                            setInfoSDPModal(true);
                          }}
                          className="mt-xs ml-sm cursorPointer"
                        />
                      )}
                    </Box>
                    {!openAccordian ? (
                      <DownArrow
                        className="cursorPointer"
                        style={{ color: '#008C82' }}
                        onClick={() => {
                          setOpenAccordian(true);
                        }}
                      />
                    ) : (
                      <UpArrow
                        className="cursorPointer"
                        onClick={() => {
                          setOpenAccordian(false);
                        }}
                      />
                    )}
                  </Box>
                  {openAccordian &&
                    ifspSDPAndConsent.serviceDelivery?.length > 0 && (
                      <Box component="div">
                        {ifspSDPAndConsent.serviceDelivery.map(
                          (__, subSectionIndex) => (
                            <Box
                              component="div"
                              key={`service-delivery-plan-sub-section-${
                                subSectionIndex + 1
                              }`}
                              className="mb-lg"
                            >
                              <Box
                                component="div"
                                className="flex__justify__space-between mt-lg mb-lg"
                                sx={{
                                  paddingBottom:
                                    showSubSection &&
                                    openIndex === subSectionIndex
                                      ? '0px'
                                      : '24px',
                                  borderBottom:
                                    showSubSection &&
                                    openIndex === subSectionIndex
                                      ? 'none'
                                      : '1px solid #00C6B8',
                                }}
                              >
                                <Box
                                  component="div"
                                  className="flex__ align__items__center"
                                >
                                  {subSectionIndex === 0 ? (
                                    <MediumTypography
                                      labelid="IFSP.addService"
                                      defaultlabel="Add Services"
                                      textColor="#2A4241"
                                      fontweight={600}
                                      fontSize="16px"
                                      className="mr-sm ml-sm"
                                    />
                                  ) : (
                                    <MediumTypography
                                      labelid="IFSP.earlyInterventionServicesForSSPAutism"
                                      defaultlabel="Early Intervention Services for SSP Autism"
                                      textColor="#2A4241"
                                      fontweight={600}
                                      fontSize="16px"
                                      className="mr-sm ml-sm"
                                    />
                                  )}
                                </Box>
                                <Box
                                  component="div"
                                  className="flex__ justifyContent-FlexEnd  align__items__center"
                                >
                                  {(!showSubSection ||
                                    openIndex !== subSectionIndex) &&
                                    ifspSDPAndConsent.serviceDelivery[
                                      subSectionIndex
                                    ].id &&
                                    ifspSDPAndConsent.serviceDelivery[
                                      subSectionIndex
                                    ].servicePlan.some(
                                      (sp) => sp.tbrUpdateRequired === true,
                                    ) &&
                                    !ifspMainPageDetails.isServiceDeliveryChecked && (
                                      <MediumTypography
                                        labelid="IFSP.ChildOutcomeTBRSync.Warning"
                                        defaultlabel="Data is not fully synchronized with TBR"
                                        textColor="#F96300"
                                      />
                                    )}
                                  {subSectionIndex !== 0 &&
                                    !ifspSDPAndConsent.isLocked &&
                                    (ifspSDPAndConsent.serviceDelivery[
                                      subSectionIndex
                                    ].servicePlan.some(
                                      (i) => i.tbrServicePlanId < 1,
                                    ) ||
                                      ifspSDPAndConsent.serviceDelivery[
                                        subSectionIndex
                                      ].servicePlan.length === 0) && (
                                      <Box
                                        component="div"
                                        className="cursorPointer flex__ align__items__center ml-lg"
                                        onClick={() => {
                                          if (!ifspSDPAndConsent.isLocked) {
                                            setOpenSDPDeleteWarningModal(true);
                                          }
                                        }}
                                      >
                                        <Delete />
                                        <MediumTypography
                                          labelid="IFSP.Delete"
                                          defaultlabel="Delete"
                                          textColor="#EB4C60"
                                          fontweight={700}
                                          fontSize="16px"
                                          className="ml-xs"
                                        />
                                      </Box>
                                    )}
                                  {showSubSection &&
                                  openIndex === subSectionIndex ? (
                                    <UpArrow
                                      onClick={() => {
                                        setShowSubSection(false);
                                        setIsEdit(false);
                                        setOpenIndex(subSectionIndex);
                                      }}
                                      className="cursorPointer ml-lg"
                                    />
                                  ) : (
                                    <DownArrow
                                      onClick={() => {
                                        formikRef.current?.resetForm();
                                        setShowSubSection(true);
                                        setIsEdit(false);
                                        setOpenIndex(subSectionIndex);
                                      }}
                                      className="cursorPointer ml-lg"
                                    />
                                  )}
                                </Box>
                              </Box>
                              {showSubSection &&
                                openIndex === subSectionIndex && (
                                  <Box
                                    component="div"
                                    sx={{
                                      borderBottom: '1px solid #00C6B8',
                                    }}
                                  >
                                    <Grid container rowSpacing={'24px'}>
                                      <Grid item xs={5}>
                                        <DropdownComponent
                                          disabled={
                                            !ifspPermission.hasClientEditPermission ||
                                            ((ifspSDPAndConsent.isLocked ||
                                              values.tbrServicePlanId > 0) &&
                                              (!ifspPermission.hasIfspEditPermissionAfterLock ||
                                                !isEdit))
                                          }
                                          names={
                                            subSectionIndex === 0
                                              ? extractedEiService
                                              : eiServiceAutism
                                          }
                                          defaultlabelid="EI Service/Method"
                                          labelid="IFSP.EIService/Method"
                                          value={values.eiService}
                                          handleChange={(value: string) => {
                                            if (
                                              EXCLUDED_CODES.includes(value)
                                            ) {
                                              setFieldValue('providedBy', '');
                                            }
                                            setFieldValue('eiService', value);
                                          }}
                                          Required
                                        />
                                        {errors.eiService &&
                                          touched.eiService && (
                                            <MediumTypography
                                              className="errorText-md"
                                              labelid={errors.eiService}
                                              defaultlabel="EI Service/Method is Required"
                                            />
                                          )}
                                      </Grid>
                                      <Grid item xs={0.5}></Grid>
                                      <Grid item xs={5}>
                                        <DropdownComponent
                                          disabled={
                                            !ifspPermission.hasClientEditPermission ||
                                            ((ifspSDPAndConsent.isLocked ||
                                              values.tbrServicePlanId > 0) &&
                                              (!ifspPermission.hasIfspEditPermissionAfterLock ||
                                                !isEdit)) ||
                                            EXCLUDED_CODES.includes(
                                              values.eiService,
                                            )
                                          }
                                          names={
                                            subSectionIndex === 0
                                              ? providedBy
                                              : providedByAutism
                                          }
                                          value={values.providedBy}
                                          defaultlabelid="Provided by (Discipline Responsibile)"
                                          labelid="IFSP.provided"
                                          handleChange={(value: string) => {
                                            setFieldValue('providedBy', value);
                                          }}
                                          Required={
                                            !EXCLUDED_CODES.includes(
                                              values.eiService,
                                            )
                                          }
                                        />
                                        {errors.providedBy &&
                                          touched.providedBy && (
                                            <MediumTypography
                                              className="errorText-md"
                                              labelid={errors.providedBy}
                                              defaultlabel="Provided by is Required"
                                            />
                                          )}
                                      </Grid>
                                      <Grid item xs={5}>
                                        <DropdownComponent
                                          disabled={
                                            !ifspPermission.hasClientEditPermission ||
                                            ((ifspSDPAndConsent.isLocked ||
                                              values.tbrServicePlanId > 0) &&
                                              (!ifspPermission.hasIfspEditPermissionAfterLock ||
                                                !isEdit))
                                          }
                                          names={locationOfService}
                                          value={values.locationOfService}
                                          defaultlabelid="Location of Service"
                                          labelid="ifsp.locationOfService"
                                          handleChange={(value: string) => {
                                            setFieldValue(
                                              'locationOfService',
                                              value,
                                            );
                                          }}
                                          Required
                                        />
                                        {errors.locationOfService &&
                                          touched.locationOfService && (
                                            <MediumTypography
                                              className="errorText-md"
                                              labelid={errors.locationOfService}
                                              defaultlabel="Location of Service is Required"
                                            />
                                          )}
                                      </Grid>
                                      <Grid item xs={0.5}></Grid>
                                      <Grid item xs={5}>
                                        <Textinput
                                          disabled={
                                            !ifspPermission.hasClientEditPermission ||
                                            ((ifspSDPAndConsent.isLocked ||
                                              values.tbrServicePlanId > 0) &&
                                              (!ifspPermission.hasIfspEditPermissionAfterLock ||
                                                !isEdit))
                                          }
                                          className="bg__white"
                                          name="frequencyAndLength"
                                          Value={
                                            values.frequency
                                              ? String(values.frequency)
                                              : ''
                                          }
                                          labelid="IFSP.frequency"
                                          defaultlabelid="Frequency (In Numbers)"
                                          inputProps={{
                                            className: 'translate',
                                            maxLength: 500,
                                          }}
                                          Required
                                          handlechange={(value: string) => {
                                            const filteredValue = value.replace(
                                              /\D/g,
                                              '',
                                            );

                                            setFieldValue(
                                              'frequency',
                                              Number(filteredValue),
                                            );
                                          }}
                                        />
                                        {errors.frequency &&
                                          touched.frequency && (
                                            <MediumTypography
                                              className="errorText-md"
                                              labelid={errors.frequency}
                                              defaultlabel="Frequency is Required"
                                            />
                                          )}
                                      </Grid>
                                      <Grid item xs={5}>
                                        <DropdownComponent
                                          disabled={
                                            !ifspPermission.hasClientEditPermission ||
                                            ((ifspSDPAndConsent.isLocked ||
                                              values.tbrServicePlanId > 0) &&
                                              (!ifspPermission.hasIfspEditPermissionAfterLock ||
                                                !isEdit))
                                          }
                                          names={period}
                                          value={values.period}
                                          defaultlabelid="Period"
                                          labelid="IFSP.period"
                                          handleChange={(value: string) => {
                                            setFieldValue('period', value);
                                          }}
                                          Required
                                        />
                                        {errors.period && touched.period && (
                                          <MediumTypography
                                            className="errorText-md"
                                            labelid={errors.period}
                                            defaultlabel="Period is Required"
                                          />
                                        )}
                                      </Grid>
                                      <Grid item xs={0.5}></Grid>
                                      <Grid item xs={5}>
                                        <DurationPicker
                                          uniqueId={`lengthduration-${subSectionIndex}`}
                                          placeholderRequired={
                                            values.length === null
                                          }
                                          disabled={
                                            !ifspPermission.hasClientEditPermission ||
                                            ((ifspSDPAndConsent.isLocked ||
                                              values.tbrServicePlanId > 0) &&
                                              (!ifspPermission.hasIfspEditPermissionAfterLock ||
                                                !isEdit))
                                          }
                                          labelId={'IFSP.length'}
                                          interval={25}
                                          backgroundColor="#fff"
                                          defaultLabel={'Length (In Hours)'}
                                          maxDuration={{
                                            hour: 24,
                                            minutes: 0,
                                          }}
                                          required
                                          duration={
                                            values.length
                                              ? formatStringDateInDecimals(
                                                  formattedLength(
                                                    values.length,
                                                  ),
                                                )
                                              : formatStringDateInDecimals('')
                                          }
                                          setDuration={(duration) => {
                                            setFieldValue(
                                              'length',
                                              formatTimeDurationInDecimals(
                                                duration,
                                                '.',
                                              ),
                                            );
                                          }}
                                        />
                                        {errors.length && touched.length && (
                                          <MediumTypography
                                            className="errorText-md"
                                            labelid={errors.length}
                                            defaultlabel="Length is Required"
                                          />
                                        )}
                                      </Grid>

                                      <Grid item xs={5}>
                                        <DropdownComponent
                                          disabled={
                                            !ifspPermission.hasClientEditPermission ||
                                            ((ifspSDPAndConsent.isLocked ||
                                              values.tbrServicePlanId > 0) &&
                                              (!ifspPermission.hasIfspEditPermissionAfterLock ||
                                                !isEdit))
                                          }
                                          names={intensity}
                                          value={values.intensity}
                                          defaultlabelid="Intensity - Individual/Group"
                                          labelid="IFSP.intensity-IndividualGroup"
                                          handleChange={(value: string) => {
                                            setFieldValue('intensity', value);
                                          }}
                                          Required
                                        />
                                        {errors.intensity &&
                                          touched.intensity && (
                                            <MediumTypography
                                              className="errorText-md"
                                              labelid={errors.intensity}
                                              defaultlabel="Intensity is Required"
                                            />
                                          )}
                                      </Grid>
                                      <Grid item xs={0.5}></Grid>
                                      <Grid item xs={5}>
                                        <Textinput
                                          disabled={
                                            !ifspPermission.hasClientEditPermission ||
                                            ((ifspSDPAndConsent.isLocked ||
                                              values.tbrServicePlanId > 0) &&
                                              (!ifspPermission.hasIfspEditPermissionAfterLock ||
                                                !isEdit))
                                          }
                                          className="bg__white"
                                          name="durationofService"
                                          Value={values.durationOfService}
                                          labelid="IFSP.durationOfService"
                                          defaultlabelid="Duration of Service (Ex: 1 week, 2 months)"
                                          inputProps={{
                                            className: 'translate',
                                            maxLength: 500,
                                          }}
                                          handlechange={(value: string) => {
                                            setFieldValue(
                                              'durationOfService',
                                              value,
                                            );
                                          }}
                                          Required
                                        />
                                        {errors.durationOfService &&
                                          touched.durationOfService && (
                                            <MediumTypography
                                              className="errorText-md"
                                              labelid={errors.durationOfService}
                                              defaultlabel="Duration of Service is Required"
                                            />
                                          )}
                                      </Grid>
                                      <Grid item xs={5}>
                                        <DatePickerComponent
                                          disabledDate={
                                            !ifspPermission.hasClientEditPermission ||
                                            ((ifspSDPAndConsent.isLocked ||
                                              values.tbrServicePlanId > 0) &&
                                              (!ifspPermission.hasIfspEditPermissionAfterLock ||
                                                !isEdit))
                                          }
                                          className="bg__white"
                                          labelid="IFSP.startDate"
                                          disableFuture={false}
                                          defaultlabelid="Start Date"
                                          value={
                                            values.startDate
                                              ? dayjs(values.startDate)
                                              : null
                                          }
                                          required={true}
                                          handlechange={(
                                            dateValue: Dayjs | null,
                                          ) => {
                                            setFieldValue('endDate', '');
                                            const formattedDate =
                                              dayjs(dateValue).format(
                                                'MM/DD/YYYY',
                                              );
                                            if (dateValue === null) {
                                              setFieldValue('startDate', null);
                                            } else {
                                              setFieldValue(
                                                'startDate',
                                                formattedDate,
                                              );
                                            }
                                          }}
                                        />
                                        {errors.startDate &&
                                          touched.startDate && (
                                            <MediumTypography
                                              className="errorText-md"
                                              labelid={errors.startDate}
                                              defaultlabel="Start Date is Required"
                                            />
                                          )}
                                        {values.startDate && (
                                          <MediumTypography
                                            fontweight={600}
                                            fontStyle="italic"
                                            className="mt-xs"
                                            labelid={
                                              'IFSP.serviceDeliveryMustBeDeliveredWithin30Days'
                                            }
                                            defaultlabel="*This service must be delivered within 30 days of the Start Date; otherwise, the Reason for Late is required"
                                          />
                                        )}
                                      </Grid>
                                      <Grid item xs={0.5}></Grid>

                                      <Grid item xs={5}>
                                        <DatePickerComponent
                                          disabledDate={
                                            !ifspPermission.hasClientEditPermission ||
                                            ((ifspSDPAndConsent.isLocked ||
                                              !values.startDate) &&
                                              (!ifspPermission.hasIfspEditPermissionAfterLock ||
                                                !isEdit))
                                          }
                                          minDate={dayjs(values.startDate)}
                                          maxDate={dayjs()}
                                          className="bg__white"
                                          labelid="IFSP.endDate"
                                          disableFuture={false}
                                          defaultlabelid="End Date"
                                          value={
                                            values.endDate
                                              ? dayjs(values.endDate)
                                              : null
                                          }
                                          handlechange={(
                                            dateValue: Dayjs | null,
                                          ) => {
                                            const formattedDate =
                                              dayjs(dateValue).format(
                                                'MM/DD/YYYY',
                                              );
                                            if (dateValue === null) {
                                              setFieldValue('endDate', null);
                                            } else {
                                              setFieldValue(
                                                'endDate',
                                                formattedDate,
                                              );
                                            }
                                          }}
                                        />
                                        {errors.endDate && touched.endDate && (
                                          <MediumTypography
                                            className="errorText-md"
                                            labelid={errors.endDate}
                                            defaultlabel="End Date is Required"
                                          />
                                        )}
                                      </Grid>
                                      <Grid item xs={5}>
                                        <DropdownComponent
                                          disabled={
                                            !ifspPermission.hasClientEditPermission ||
                                            (ifspSDPAndConsent.isLocked &&
                                              (!ifspPermission.hasIfspEditPermissionAfterLock ||
                                                !isEdit))
                                          }
                                          labelid="IFSP.reasonForLate"
                                          defaultlabelid="Reason for Late"
                                          names={fliteredLookUpReasons}
                                          value={values.reasonForLate}
                                          handleChange={(value: string) => {
                                            setFieldValue(
                                              'reasonForLate',
                                              value,
                                            );
                                          }}
                                        />
                                      </Grid>
                                      <Grid
                                        item
                                        className="flex__ pl-md mt-xs po cursorPointer"
                                        onClick={() => {
                                          if (
                                            (!ifspSDPAndConsent.isLocked &&
                                              ifspPermission.hasClientEditPermission) ||
                                            (ifspSDPAndConsent.isLocked &&
                                              ifspPermission.hasIfspEditPermissionAfterLock &&
                                              ifspPermission.hasClientEditPermission &&
                                              isEdit)
                                          ) {
                                            setOpenedSubSectionIndex(
                                              subSectionIndex,
                                            );
                                            handleSubmit();
                                          }
                                        }}
                                      >
                                        <AddNew />
                                        <MediumTypography
                                          className="ml-xs"
                                          labelid={addlabelid}
                                          defaultlabel={adddefaultlabel}
                                        />
                                      </Grid>
                                    </Grid>

                                    <Box
                                      component="div"
                                      className="respoScrollTable mt-lg mb-md"
                                    >
                                      <TableContainer component={Paper}>
                                        <Table>
                                          <TableHeader
                                            className="listDataTableHead"
                                            headerNames={headCells}
                                            checkBoxRequired={false}
                                            order={order}
                                            orderBy={orderBy}
                                            onRequestSort={handleRequestSort}
                                          />
                                          <TableBody className="tableRowcss">
                                            {openIndex === subSectionIndex &&
                                              ifspSDPAndConsent.serviceDelivery[
                                                subSectionIndex
                                              ].servicePlan.map(
                                                (
                                                  listData: ServicePlanType,
                                                  index,
                                                ) => (
                                                  <ServiceDeliveryPlanTableRow
                                                    isIfspScreenLocked={
                                                      ifspSDPAndConsent.isLocked
                                                    }
                                                    canEditAfterLock={
                                                      ifspPermission.hasIfspEditPermissionAfterLock &&
                                                      ifspPermission.hasClientEditPermission
                                                    }
                                                    canDeleteAfterLock={
                                                      ifspPermission.hasIfspDeletePermissionAfterLock &&
                                                      ifspPermission.hasClientEditPermission
                                                    }
                                                    actionMode={
                                                      isEdit ? 'update' : 'add'
                                                    }
                                                    key={`${listData.id}-${index}-${subSectionIndex}`}
                                                    dataLength={
                                                      ifspSDPAndConsent
                                                        .serviceDelivery[
                                                        subSectionIndex
                                                      ].servicePlan.length
                                                    }
                                                    actionRequired={true}
                                                    data={listData}
                                                    onEdit={() => {
                                                      if (
                                                        !ifspSDPAndConsent.isLocked ||
                                                        (ifspPermission.hasIfspEditPermissionAfterLock &&
                                                          ifspPermission.hasClientEditPermission)
                                                      ) {
                                                        onEditRow(
                                                          subSectionIndex,
                                                          index,
                                                        );
                                                      }
                                                    }}
                                                    onDelete={(id: string) => {
                                                      if (
                                                        (!ifspSDPAndConsent.isLocked &&
                                                          values.tbrServicePlanId <
                                                            1) ||
                                                        (ifspPermission.hasIfspDeletePermissionAfterLock &&
                                                          ifspPermission.hasClientEditPermission &&
                                                          ifspSDPAndConsent
                                                            .serviceDelivery[
                                                            subSectionIndex
                                                          ].servicePlan.length >
                                                            1)
                                                      ) {
                                                        onDeleteRowData(
                                                          id,
                                                          subSectionIndex,
                                                          index,
                                                        );
                                                      }
                                                    }}
                                                    reasonIfLate={lookUpReasons}
                                                    eiService={eiService}
                                                    intensity={intensity}
                                                    locationOfService={
                                                      locationOfService
                                                    }
                                                    period={period}
                                                    providedBy={
                                                      providerDiscipline
                                                    }
                                                  />
                                                ),
                                              )}
                                          </TableBody>
                                        </Table>
                                        {ifspSDPAndConsent.serviceDelivery[
                                          subSectionIndex
                                        ].servicePlan.length === 0 && (
                                          <Box
                                            component="div"
                                            className="width__100"
                                          >
                                            <MediumTypography
                                              textstyle={'center'}
                                              labelid="IFSP.emptyMessage"
                                              defaultlabel="No data added yet"
                                              className="p-sm"
                                            />
                                          </Box>
                                        )}
                                      </TableContainer>
                                    </Box>
                                    <MediumTypography
                                      labelid="IFSP.justification"
                                      defaultlabel="Include a justification for any EI service (determined by the guardian and the IFSP team) that will not occur in the child's natural environment."
                                      fontweight={400}
                                      textColor="#2A4241"
                                      className="pt-md"
                                    />
                                    <CommentTextbox
                                      name={'IncludeAJustification'}
                                      className="translate"
                                      disabled={
                                        (ifspSDPAndConsent.isLocked &&
                                          (!ifspPermission.hasIfspEditPermissionAfterLock ||
                                            !ifspPermission.hasClientEditPermission)) ||
                                        !ifspPermission.hasClientEditPermission
                                      }
                                      placeholder="Enter your comments"
                                      Value={
                                        ifspSDPAndConsent.serviceDelivery[
                                          subSectionIndex
                                        ].eiServiceJustificationComment
                                      }
                                      handlechange={(value) => {
                                        dispatch(
                                          updateIfspSDPAndConsent({
                                            ...ifspSDPAndConsent,
                                            serviceDelivery:
                                              ifspSDPAndConsent.serviceDelivery.map(
                                                (item, index) =>
                                                  index === subSectionIndex
                                                    ? {
                                                        ...item,
                                                        eiServiceJustificationComment:
                                                          value || null,
                                                      }
                                                    : item,
                                              ),
                                          }),
                                        );
                                      }}
                                      rows={3}
                                      style={TextAreaStyle}
                                      maxLength={500}
                                    />
                                    <MediumTypography
                                      defaultlabel="Other Services"
                                      labelid="IFSP.otherServices"
                                      sxProps={{
                                        marginTop: '16px',
                                        marginBottom: '8px',
                                      }}
                                      fontSize="16px"
                                      fontweight={600}
                                    />
                                    <MediumTypography
                                      labelid="IFSP.servicesAndSupports"
                                      defaultlabel="What other services and supports (medical, recreational, religious, social and other child-related activities) do we have or need that are not required or funded by EI?"
                                      fontweight={400}
                                      textColor="#2A4241"
                                      className="pt-md"
                                    />
                                    <CommentTextbox
                                      name={'ServicesAndSupports'}
                                      className="translate"
                                      disabled={
                                        (ifspSDPAndConsent.isLocked &&
                                          (!ifspPermission.hasIfspEditPermissionAfterLock ||
                                            !ifspPermission.hasClientEditPermission)) ||
                                        !ifspPermission.hasClientEditPermission
                                      }
                                      placeholder="Enter your comments"
                                      Value={
                                        ifspSDPAndConsent.serviceDelivery[
                                          subSectionIndex
                                        ].serviceSupportComment
                                      }
                                      handlechange={(value) => {
                                        dispatch(
                                          updateIfspSDPAndConsent({
                                            ...ifspSDPAndConsent,
                                            serviceDelivery:
                                              ifspSDPAndConsent.serviceDelivery.map(
                                                (item, index) =>
                                                  index === subSectionIndex
                                                    ? {
                                                        ...item,
                                                        serviceSupportComment:
                                                          value || null,
                                                      }
                                                    : item,
                                              ),
                                          }),
                                        );
                                      }}
                                      rows={3}
                                      style={TextAreaStyle}
                                      maxLength={500}
                                    />
                                    <MediumTypography
                                      labelid="IFSP.stepsneeded"
                                      defaultlabel="What steps might the service coordinator or family take to get the services and supports needed?"
                                      fontweight={400}
                                      textColor="#2A4241"
                                      className="pt-md"
                                    />
                                    <CommentTextbox
                                      name={'StepsNeeded'}
                                      className="translate mb-md"
                                      disabled={
                                        (ifspSDPAndConsent.isLocked &&
                                          (!ifspPermission.hasIfspEditPermissionAfterLock ||
                                            !ifspPermission.hasClientEditPermission)) ||
                                        !ifspPermission.hasClientEditPermission
                                      }
                                      placeholder="Enter your comments"
                                      Value={
                                        ifspSDPAndConsent.serviceDelivery[
                                          subSectionIndex
                                        ].serviceSupportNeededComment
                                      }
                                      handlechange={(value) => {
                                        dispatch(
                                          updateIfspSDPAndConsent({
                                            ...ifspSDPAndConsent,
                                            serviceDelivery:
                                              ifspSDPAndConsent.serviceDelivery.map(
                                                (item, index) =>
                                                  index === subSectionIndex
                                                    ? {
                                                        ...item,
                                                        serviceSupportNeededComment:
                                                          value || null,
                                                      }
                                                    : item,
                                              ),
                                          }),
                                        );
                                      }}
                                      rows={3}
                                      style={TextAreaStyle}
                                      maxLength={500}
                                    />
                                  </Box>
                                )}
                            </Box>
                          ),
                        )}
                        <ButtonComponent
                          disabled={
                            ifspSDPAndConsent.serviceDelivery.length >= 2 ||
                            ifspSDPAndConsent.isLocked ||
                            !ifspPermission.hasClientEditPermission
                          }
                          className="btn-primary btn-submit"
                          variantType="contained"
                          type="submit"
                          labelId="Ifsp.sdpButton"
                          defaultLabelId="Add EIBI SSP Services"
                          onClick={() => {
                            addSubSection();
                          }}
                        />
                        <MediumTypography
                          labelid="Ifsp.sdpHelpText"
                          defaultlabel="Add Additional Service Delivery Plan for EIBI SSP Service (Autism, Down Syndrome)"
                          fontSize="12px"
                          fontweight={400}
                          textColor="#2A4241"
                          fontStyle="italic"
                        />
                      </Box>
                    )}
                </Box>
              </Box>
            )}
          </Formik>
        </Box>
      </Box>
    </Box>
  );
};

export default ServiceDeliveryPlan;
